import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { StatusBadge } from '@/components/common/StatusBadge';
import { DataTable } from '@/components/common/DataTable';
import { UserAvatar } from '@/components/common/UserAvatar';
import { PayeeSelector } from '@/components/PayeeSelector';
import ReplyComposeDialog from '@/components/common/ReplyComposeDialog';
import { ticketAPI, userAPI, agentAPI } from '@/lib/api';
import { isKRRole, formatDate, formatDateTime, TICKET_CATEGORIES, TICKET_PRIORITIES } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Plus,
  MessageSquare,
  Loader2,
  Edit,
  Trash2,
  Send,
  User,
  CheckSquare,
  Calendar,
  X
} from 'lucide-react';

export default function TicketsPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [agentOptions, setAgentOptions] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [newNoteInternal, setNewNoteInternal] = useState(false);   // 27.53.E — visibility toggle
  const [inboundAddress, setInboundAddress] = useState(null);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replySeed, setReplySeed] = useState(null);
  const [loadingNotes, setLoadingNotes] = useState(false);
  // Task state
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', assignee_user_id: '', due_date: '' });
  const [detailTab, setDetailTab] = useState('notes');
  const [ticketSearch, setTicketSearch] = useState('');
  const [ticketFilters, setTicketFilters] = useState({});
  
  const [formData, setFormData] = useState({
    category: '',
    request_type: '',
    subject: '',
    description: '',
    npn: '',
    priority: 'medium',
    assigned_to_user_id: '',
  });
  const [updateForm, setUpdateForm] = useState({
    status: '',
    assigned_to_user_id: '',
    priority: '',
    category: '',
    npn: ''
  });

  const isKR = isKRRole(user);

  useEffect(() => {
    loadData();
    if (searchParams.get('action') === 'create') {
      const npn = searchParams.get('npn');
      if (npn) {
        setFormData(prev => ({ 
          ...prev, 
          npn, 
          category: 'Contract',
          subject: `NPN Access Request: ${npn}`,
          description: `Requesting access to agent with NPN ${npn}. This NPN exists outside my agency tree.`
        }));
      }
      setShowDialog(true);
    }
    const viewId = searchParams.get('view');
    if (viewId) {
      openDetail({ ticket_id: viewId });
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      const [ticketsRes, agentsRes] = await Promise.all([
        ticketAPI.list(),
        agentAPI.simpleList(),
      ]);
      setTickets(ticketsRes.data);
      setAgentOptions((agentsRes.data || []).map(a => ({ value: a.npn, label: a.name })));
      
      if (isKR) {
        const usersRes = await userAPI.list();
        setUsers(usersRes.data);
      }
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const loadNotes = async (ticketId) => {
    setLoadingNotes(true);
    try {
      const response = await ticketAPI.getNotes(ticketId);
      setNotes(response.data);
    } catch (error) {
      console.error('Failed to load notes:', error);
    } finally {
      setLoadingNotes(false);
    }
  };

  const loadTasks = async (ticketId) => {
    setLoadingTasks(true);
    try {
      const response = await ticketAPI.listTasks(ticketId);
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to load tasks:', error);
    } finally {
      setLoadingTasks(false);
    }
  };

  const ticketFilterDefs = useMemo(() => {
    const statuses = [...new Set(tickets.map(t => t.status).filter(Boolean))].sort()
      .map(s => ({ value: s, label: s.replace(/_/g, ' ') }));
    const categories = TICKET_CATEGORIES.map(c => ({ value: c.value, label: c.label }));
    const defs = [
      { key: 'status', label: 'Status', options: statuses },
      { key: 'category', label: 'Category', options: categories },
      { key: 'priority', label: 'Priority', options: TICKET_PRIORITIES.map(p => ({ value: p.value, label: p.label })) },
    ];
    if (agentOptions.length > 0) {
      defs.push({ key: 'agent_npn', label: 'Agent', options: agentOptions });
    }
    // Assignee filter — only useful for KR viewers with the users list loaded.
    if (isKR && users.length > 0) {
      const krUsers = users.filter(u => u.roles?.some(r => r.role === 'KR_SuperAdmin' || r.role === 'KR_AdminOps'));
      defs.push({
        key: 'assigned_to_user_id',
        label: 'Assignee',
        options: [
          { value: '__unassigned__', label: 'Unassigned' },
          ...krUsers.map(u => ({ value: u.user_id, label: u.name })),
        ],
      });
    }
    return defs;
  }, [tickets, agentOptions, isKR, users]);

  const ticketFilterFn = useCallback((row, fv) => {
    if (fv.status && fv.status !== 'all' && row.status !== fv.status) return false;
    if (fv.category && fv.category !== 'all' && row.category !== fv.category) return false;
    if (fv.priority && fv.priority !== 'all' && row.priority !== fv.priority) return false;
    if (fv.agent_npn && fv.agent_npn !== 'all' && row.npn !== fv.agent_npn) return false;
    if (fv.assigned_to_user_id && fv.assigned_to_user_id !== 'all') {
      if (fv.assigned_to_user_id === '__unassigned__') {
        if (row.assigned_to_user_id) return false;
      } else if (row.assigned_to_user_id !== fv.assigned_to_user_id) {
        return false;
      }
    }
    return true;
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await ticketAPI.create(formData);
      toast.success('Ticket created successfully');
      setShowDialog(false);
      setFormData({ category: '', subject: '', description: '', npn: '', priority: 'medium' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ticket');
    }
  };

  const handleUpdate = async () => {
    if (!selectedTicket) return;
    try {
      await ticketAPI.update(selectedTicket.ticket_id, updateForm);
      toast.success('Ticket updated');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update ticket');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedTicket) return;
    try {
      await ticketAPI.addNote(selectedTicket.ticket_id, newNote, newNoteInternal ? 'internal' : 'visible');
      setNewNote('');
      setNewNoteInternal(false);
      loadNotes(selectedTicket.ticket_id);
      toast.success('Note added');
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  // Phase 27.53.E — Snooze presets. `until = null` un-snoozes.
  const handleSnooze = async (until) => {
    if (!selectedTicket) return;
    try {
      const { data } = await ticketAPI.snooze(selectedTicket.ticket_id, until);
      setSelectedTicket(data);
      // Refresh the list so the row chip updates too.
      loadData();
      toast.success(until ? 'Reminders snoozed' : 'Snooze cleared');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Snooze failed');
    }
  };

  const handleFlipVisibility = async (note) => {
    if (!selectedTicket) return;
    const next = note.visibility === 'internal' ? 'visible' : 'internal';
    try {
      await ticketAPI.updateNoteVisibility(selectedTicket.ticket_id, note.note_id, next);
      loadNotes(selectedTicket.ticket_id);
      toast.success(`Note now ${next === 'internal' ? 'internal (KR only)' : 'visible to agent'}`);
    } catch {
      toast.error('Could not update visibility');
    }
  };

  const handleAddTask = async () => {
    if (!newTask.title.trim() || !selectedTicket) return;
    try {
      const taskData = {
        title: newTask.title,
        assignee_user_id: newTask.assignee_user_id || null,
        due_date: newTask.due_date || null
      };
      await ticketAPI.createTask(selectedTicket.ticket_id, taskData);
      setNewTask({ title: '', assignee_user_id: '', due_date: '' });
      setShowTaskForm(false);
      loadTasks(selectedTicket.ticket_id);
      toast.success('Task added');
    } catch (error) {
      toast.error('Failed to add task');
    }
  };

  const handleToggleTask = async (task) => {
    if (!selectedTicket) return;
    try {
      const newStatus = task.status === 'completed' ? 'pending' : 'completed';
      await ticketAPI.updateTask(selectedTicket.ticket_id, task.task_id, { status: newStatus });
      loadTasks(selectedTicket.ticket_id);
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!selectedTicket) return;
    try {
      await ticketAPI.deleteTask(selectedTicket.ticket_id, taskId);
      loadTasks(selectedTicket.ticket_id);
      toast.success('Task deleted');
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const handleDelete = async () => {
    if (!ticketToDelete) return;
    try {
      await ticketAPI.delete(ticketToDelete.ticket_id);
      toast.success('Ticket deleted');
      setShowDeleteDialog(false);
      setTicketToDelete(null);
      setShowDetailDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete ticket');
    }
  };

  const openDetail = async (ticket) => {
    // 27.53.D — GET server-side flips Open→In_Progress for KR viewers
    // and returns the fresh state (with reminder_count, snoozed_until,
    // etc.). Fall back to the row data if the fetch fails.
    let fresh = ticket;
    try {
      const r = await ticketAPI.get(ticket.ticket_id);
      fresh = r.data;
      // Reflect the flip in the list too.
      setTickets((prev) => prev.map((t) => t.ticket_id === fresh.ticket_id ? fresh : t));
    } catch { /* ignore — use row data */ }
    setSelectedTicket(fresh);
    setUpdateForm({
      status: fresh.status,
      assigned_to_user_id: fresh.assigned_to_user_id || '',
      priority: fresh.priority || 'medium',
      category: fresh.category || '',
      npn: fresh.npn || '',
    });
    setNotes([]);
    setTasks([]);
    setDetailTab('notes');
    setNewNoteInternal(false);
    loadNotes(fresh.ticket_id);
    loadTasks(fresh.ticket_id);
    // 27.53.E — Fetch the ticket's unique inbound email address for
    // the copy-to-clipboard chip in the header.
    setInboundAddress(null);
    ticketAPI.getInboundAddress(fresh.ticket_id)
      .then((r) => setInboundAddress(r.data))
      .catch(() => setInboundAddress(null));
    setShowDetailDialog(true);
  };

  const getKRUsers = () => {
    return users.filter(u => u.roles?.some(r => r.role === 'KR_SuperAdmin' || r.role === 'KR_AdminOps'));
  };

  const getAllUsers = () => {
    return users;
  };

  // Phase 27.53.D+E — SLA chip: red URGENT when an unopened ticket has
  // been sitting for ≥4h, amber ATTENTION between 2h and 4h. If the
  // ticket is snoozed, show a blue Snoozed chip instead. Business
  // hours accuracy isn't required for the visual cue — the escalation
  // scheduler already respects ET business hours; this chip is just to
  // make the queue scream.
  const slaChip = (row) => {
    // Snoozed takes priority — reminders paused, show regardless of status.
    if (row.snoozed_until && new Date(row.snoozed_until) > new Date()) {
      const until = new Date(row.snoozed_until);
      return (
        <span
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-800 uppercase tracking-wide"
          title={`Snoozed until ${until.toLocaleString()}`}
          data-testid={`sla-chip-snoozed-${row.ticket_id}`}
        >
          💤 Snoozed · {until.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
        </span>
      );
    }
    if (row.status !== 'Open' || !row.created_at) return null;
    const ageMs = Date.now() - new Date(row.created_at).getTime();
    const hours = ageMs / 3600000;
    if (hours >= 4) {
      return (
        <span
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-600 text-white uppercase tracking-wide animate-pulse"
          title={`Unopened for ${hours.toFixed(1)}h`}
          data-testid={`sla-chip-urgent-${row.ticket_id}`}
        >
          ⏰ Urgent · {Math.floor(hours)}h
        </span>
      );
    }
    if (hours >= 2) {
      return (
        <span
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-500 text-white uppercase tracking-wide"
          title={`Unopened for ${hours.toFixed(1)}h`}
          data-testid={`sla-chip-attention-${row.ticket_id}`}
        >
          ⚠ Attention · {Math.floor(hours)}h
        </span>
      );
    }
    return null;
  };

  // Phase 27.53.E — Snooze preset builder. Times are computed in the
  // browser's local timezone since that's what the operator sees on
  // screen; server stores the resulting ISO.
  const snoozePresets = () => {
    const now = new Date();
    const inHours = (h) => new Date(now.getTime() + h * 3600000);
    const tomorrow8 = new Date(now); tomorrow8.setDate(now.getDate() + 1); tomorrow8.setHours(8, 0, 0, 0);
    const in2d = new Date(now); in2d.setDate(now.getDate() + 2); in2d.setHours(9, 0, 0, 0);
    return [
      { label: '+4 hours', iso: inHours(4).toISOString() },
      { label: 'Tomorrow 8am', iso: tomorrow8.toISOString() },
      { label: 'In 2 days', iso: in2d.toISOString() },
    ];
  };

  const columns = [
    {
      header: 'Subject', accessor: 'subject', sortable: true,
      cell: (row) => (
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium text-slate-900">{row.subject}</p>
            {slaChip(row)}
          </div>
          <p className="text-xs text-slate-500">{row.category}</p>
        </div>
      )
    },
    {
      header: 'Status', accessor: 'status', sortable: true,
      cell: (row) => <StatusBadge status={row.status} />
    },
    {
      header: 'Priority', accessor: 'priority', sortable: true,
      cell: (row) => {
        const p = TICKET_PRIORITIES.find(x => x.value === row.priority);
        const colors = { low: 'bg-slate-100 text-slate-600', medium: 'bg-amber-100 text-amber-700', high: 'bg-red-100 text-red-700' };
        return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[row.priority] || colors.medium}`}>{p?.label || 'Medium'}</span>;
      }
    },
    {
      header: 'Category', accessor: 'category', sortable: true,
      cell: (row) => row.category?.replace(/_/g, ' ')
    },
    {
      header: 'Agent', accessor: 'npn', sortable: true,
      cell: (row) => {
        if (!row.npn) return <span className="text-slate-400">—</span>;
        const name = agentByNpn.get(row.npn);
        return (
          <div className="min-w-0">
            <p className="text-sm text-slate-800 truncate">{name || row.npn}</p>
            {name && <p className="text-xs text-slate-500">NPN {row.npn}</p>}
          </div>
        );
      }
    },
    {
      header: 'Assignee', accessor: 'assigned_to_name', sortable: true,
      cell: (row) => {
        if (!row.assigned_to_user_id) {
          return (
            <span
              className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200"
              data-testid={`assignee-unassigned-${row.ticket_id}`}
            >
              Unassigned
            </span>
          );
        }
        return (
          <span className="text-sm text-slate-800" data-testid={`assignee-${row.ticket_id}`}>
            {row.assigned_to_name || row.assigned_to_user_id}
          </span>
        );
      }
    },
    {
      header: 'Created', accessor: 'created_at', sortable: true,
      cell: (row) => formatDate(row.created_at)
    },
    {
      header: 'Updated', accessor: 'updated_at', sortable: true,
      cell: (row) => formatDate(row.updated_at)
    },
    {
      header: 'Actions',
      cell: (row) => (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={(e) => {
            e.stopPropagation();
            openDetail(row);
          }} data-testid={`view-ticket-${row.ticket_id}`}>
            <Edit size={14} />
          </Button>
          {isKR && (
            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={(e) => {
              e.stopPropagation();
              setTicketToDelete(row);
              setShowDeleteDialog(true);
            }} data-testid={`delete-ticket-${row.ticket_id}`}>
              <Trash2 size={14} />
            </Button>
          )}
        </div>
      )
    }
  ];

  // Filter tickets by status
  const closedTickets = tickets.filter(t => t.status === 'Closed');
  const activeTickets = tickets.filter(t => t.status !== 'Closed');

  const agentByNpn = useMemo(() => {
    const m = new Map();
    for (const a of agentOptions) m.set(a.value, a.label);
    return m;
  }, [agentOptions]);

  const pendingTasks = tasks.filter(t => t.status === 'pending');
  const completedTasks = tasks.filter(t => t.status === 'completed');

  return (
    <div data-testid="tickets-page">
      <PageHeader 
        title="Support Tickets"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Tickets', href: '/tickets' }
        ]}
        actions={
          <Button onClick={() => setShowDialog(true)} data-testid="create-ticket-btn">
            <Plus size={16} className="mr-2" />
            Create Ticket
          </Button>
        }
      />
      
      <div className="page-content">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <MessageSquare size={20} className="text-slate-400" />
              {isKR ? 'All Tickets' : 'My Tickets'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : (
              <Tabs defaultValue="active" className="w-full">
                <TabsList className="mb-4">
                  <TabsTrigger value="active" data-testid="tickets-tab-active">
                    Active <span className="ml-2 text-xs opacity-70">({activeTickets.length})</span>
                  </TabsTrigger>
                  <TabsTrigger value="closed" data-testid="tickets-tab-closed">
                    Closed <span className="ml-2 text-xs opacity-70">({closedTickets.length})</span>
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="active">
                  <DataTable
                    columns={columns}
                    data={activeTickets}
                    onRowClick={openDetail}
                    emptyTitle="No active tickets"
                    emptyDescription="Everything is caught up. Nice work."
                    searchValue={ticketSearch}
                    onSearchChange={setTicketSearch}
                    searchPlaceholder="Search by subject..."
                    searchFields={['subject', 'category', 'description', 'npn']}
                    filters={ticketFilterDefs}
                    filterValues={ticketFilters}
                    onFilterChange={(key, val) => setTicketFilters(prev => ({...prev, [key]: val}))}
                    filterFn={ticketFilterFn}
                    defaultSortKey="updated_at"
                    defaultSortDir="desc"
                    defaultPageSize={25}
                  />
                </TabsContent>
                <TabsContent value="closed">
                  <DataTable
                    columns={columns}
                    data={closedTickets}
                    onRowClick={openDetail}
                    emptyTitle="No closed tickets yet"
                    emptyDescription="Resolved tickets will land here."
                    searchValue={ticketSearch}
                    onSearchChange={setTicketSearch}
                    searchPlaceholder="Search by subject..."
                    searchFields={['subject', 'category', 'description', 'npn']}
                    filters={ticketFilterDefs.filter(f => f.key !== 'status')}
                    filterValues={ticketFilters}
                    onFilterChange={(key, val) => setTicketFilters(prev => ({...prev, [key]: val}))}
                    filterFn={ticketFilterFn}
                    defaultSortKey="updated_at"
                    defaultSortDir="desc"
                    defaultPageSize={25}
                  />
                </TabsContent>
              </Tabs>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Ticket</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this ticket? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Ticket Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Create Support Ticket</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {TICKET_CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Request Type</Label>
                <Select
                  value={formData.request_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, request_type: value }))}
                >
                  <SelectTrigger data-testid="request-type-select">
                    <SelectValue placeholder="Select type (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="New_Contract">New Contract</SelectItem>
                    <SelectItem value="Level_Change">Level Change</SelectItem>
                    <SelectItem value="Transfer">Transfer</SelectItem>
                    <SelectItem value="Reinstatement">Reinstatement</SelectItem>
                    <SelectItem value="Termination">Termination</SelectItem>
                    <SelectItem value="General">General</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}
                >
                  <SelectTrigger data-testid="priority-select">
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    {TICKET_PRIORITIES.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Subject *</Label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Brief description of your issue"
                  required
                  data-testid="subject-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Related Agent (Optional)</Label>
                <PayeeSelector
                  kind="agent"
                  value={formData.npn}
                  onChange={(npn) => setFormData(prev => ({ ...prev, npn: npn || '' }))}
                  placeholder="Search by name or NPN…"
                  testId="ticket-agent-picker"
                />
                <p className="text-[10px] text-slate-500">
                  Linking an agent auto-fills the ticket's owner block and helps KR see who it's about.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe your issue in detail..."
                  rows={5}
                  required
                  data-testid="description-input"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-ticket-btn">
                Create Ticket
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Ticket Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading">Ticket Details</DialogTitle>
          </DialogHeader>
          
          {selectedTicket && (
            <div className="flex-1 overflow-y-auto space-y-4 py-4">
              <div className="space-y-1">
                <Label className="text-slate-500 text-xs">Subject</Label>
                <p className="font-medium text-slate-900">{selectedTicket.subject}</p>
              </div>

              {/* Phase 27.53.C — Owner block so KR sees at a glance who
                 filed the ticket, which agent it's about and which
                 agency it belongs to. Fields fall back gracefully to
                 dashes so an unrelated ticket still renders cleanly. */}
              <div
                className="rounded-lg border border-slate-200 bg-slate-50 p-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm"
                data-testid="ticket-owner-block"
              >
                <div>
                  <Label className="text-slate-500 text-xs">Filed by</Label>
                  <p className="font-medium text-slate-800" data-testid="ticket-created-by">
                    {selectedTicket.created_by_name || '—'}
                  </p>
                </div>
                <div>
                  <Label className="text-slate-500 text-xs">Agent</Label>
                  <p className="font-medium text-slate-800" data-testid="ticket-agent-name">
                    {selectedTicket.agent_name || (selectedTicket.npn ? `NPN ${selectedTicket.npn}` : '—')}
                  </p>
                </div>
                <div>
                  <Label className="text-slate-500 text-xs">Agency</Label>
                  <p className="font-medium text-slate-800" data-testid="ticket-agency-name">
                    {selectedTicket.agency_name || '—'}
                  </p>
                </div>
                {selectedTicket.assigned_to_name && (
                  <div className="sm:col-span-3">
                    <Label className="text-slate-500 text-xs">Assigned to</Label>
                    <p className="font-medium text-slate-800" data-testid="ticket-assignee-name">
                      {selectedTicket.assigned_to_name}
                    </p>
                  </div>
                )}
                {(selectedTicket.reminder_count ?? 0) > 0 && (
                  <div className="sm:col-span-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                    ⏰ Escalation reminders sent: <b>{selectedTicket.reminder_count}</b>
                    {selectedTicket.last_reminder_at && ` · last ${formatDateTime(selectedTicket.last_reminder_at)}`}
                  </div>
                )}
                {/* Phase 27.53.E — Snooze banner + control */}
                {selectedTicket.snoozed_until && new Date(selectedTicket.snoozed_until) > new Date() ? (
                  <div className="sm:col-span-3 text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded p-2 flex items-center justify-between gap-2" data-testid="ticket-snoozed-banner">
                    <span>
                      💤 Reminders snoozed until <b>{formatDateTime(selectedTicket.snoozed_until)}</b>
                      {selectedTicket.snoozed_by_name && ` · by ${selectedTicket.snoozed_by_name}`}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 text-blue-800 border-blue-300 hover:bg-blue-100"
                      onClick={() => handleSnooze(null)}
                      data-testid="ticket-unsnooze-btn"
                    >
                      Wake up
                    </Button>
                  </div>
                ) : (
                  isKR && ['Open', 'In_Progress'].includes(selectedTicket.status) && (
                    <div className="sm:col-span-3 flex items-center gap-2 flex-wrap" data-testid="ticket-snooze-controls">
                      <span className="text-xs text-slate-500">Snooze reminders:</span>
                      {snoozePresets().map((p) => (
                        <Button
                          key={p.label}
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => handleSnooze(p.iso)}
                          data-testid={`snooze-preset-${p.label.replace(/\s+/g,'-').toLowerCase()}`}
                        >
                          💤 {p.label}
                        </Button>
                      ))}
                    </div>
                  )
                )}
                {/* Phase 27.53.E — Ticket inbox email address (Monday-style) */}
                {inboundAddress?.address && (
                  <div className="sm:col-span-3 bg-white border border-slate-200 rounded p-2 flex items-center justify-between gap-2" data-testid="ticket-inbound-address">
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">Ticket inbox — CC or BCC to log an email as a note</div>
                      <div className="text-xs font-mono text-slate-800 truncate">{inboundAddress.address}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {isKR && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => { setReplySeed(null); setReplyOpen(true); }}
                          data-testid="ticket-compose-btn"
                        >
                          ✉ Compose
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => {
                          navigator.clipboard.writeText(inboundAddress.address);
                          toast.success('Copied');
                        }}
                        data-testid="copy-inbound-address"
                      >
                        Copy
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-slate-500 text-xs">Category</Label>
                  <p>{selectedTicket.category}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-slate-500 text-xs">Created</Label>
                  <p>{formatDateTime(selectedTicket.created_at)}</p>
                </div>
              </div>
              
              {selectedTicket.npn && (
                <div className="space-y-1">
                  <Label className="text-slate-500 text-xs">Related NPN</Label>
                  <p className="font-mono">{selectedTicket.npn}</p>
                </div>
              )}
              
              <div className="space-y-1">
                <Label className="text-slate-500 text-xs">Description</Label>
                <p className="text-slate-700 whitespace-pre-wrap bg-slate-50 p-3 rounded-lg">
                  {selectedTicket.description}
                </p>
              </div>

              <Separator />

              {/* Notes & Tasks Tabs */}
              <Tabs value={detailTab} onValueChange={setDetailTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="notes" className="flex items-center gap-2">
                    <MessageSquare size={14} />
                    Notes ({notes.length})
                  </TabsTrigger>
                  <TabsTrigger value="tasks" className="flex items-center gap-2" data-testid="tasks-tab">
                    <CheckSquare size={14} />
                    Tasks ({tasks.length})
                  </TabsTrigger>
                </TabsList>

                {/* Notes Tab Content */}
                <TabsContent value="notes" className="mt-4 space-y-3">
                  <ScrollArea className="h-48 border rounded-lg p-3">
                    {loadingNotes ? (
                      <div className="flex items-center justify-center h-full">
                        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                      </div>
                    ) : notes.length === 0 ? (
                      <p className="text-center text-slate-400 text-sm py-8">No notes yet</p>
                    ) : (
                      <div className="space-y-3">
                        {notes.map((note) => (
                          <div key={note.note_id} className={`rounded-lg p-3 ${note.visibility === 'internal' ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'}`} data-testid={`note-${note.note_id}`}>
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <User size={14} className="text-slate-400" />
                              <span className="text-sm font-medium">{note.user_name}</span>
                              {note.source === 'email' && (
                                <span className="text-[10px] font-semibold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded uppercase" data-testid={`note-source-email-${note.note_id}`}>
                                  📧 Email
                                </span>
                              )}
                              {note.source === 'email_out' && (
                                <span className="text-[10px] font-semibold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded uppercase" data-testid={`note-source-emailout-${note.note_id}`}>
                                  📤 Reply Sent
                                </span>
                              )}
                              {note.visibility === 'internal' && (
                                <span className="text-[10px] font-semibold bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded uppercase" data-testid={`note-internal-${note.note_id}`}>
                                  🔒 Internal
                                </span>
                              )}
                              <span className="text-xs text-slate-400 ml-auto">
                                {formatDateTime(note.created_at)}
                              </span>
                              {isKR && note.source === 'email' && (
                                <button
                                  type="button"
                                  onClick={() => { setReplySeed(note); setReplyOpen(true); }}
                                  className="text-[10px] text-emerald-700 hover:text-emerald-900 underline flex items-center gap-0.5"
                                  data-testid={`reply-note-${note.note_id}`}
                                >
                                  ↩ Reply
                                </button>
                              )}
                              {isKR && (
                                <button
                                  type="button"
                                  onClick={() => handleFlipVisibility(note)}
                                  className="text-[10px] text-slate-500 hover:text-slate-800 underline"
                                  data-testid={`flip-visibility-${note.note_id}`}
                                >
                                  {note.visibility === 'internal' ? 'Make visible' : 'Make internal'}
                                </button>
                              )}
                            </div>
                            {(note.source === 'email' || note.source === 'email_out') && (note.from_email || note.to_email || note.subject) && (
                              <div className="text-[11px] text-slate-500 mb-1">
                                {note.from_email && <span>From: {note.from_email}</span>}
                                {note.to_email && <span>To: {note.to_email}</span>}
                                {(note.from_email || note.to_email) && note.subject && <span> · </span>}
                                {note.subject && <span>Subject: {note.subject}</span>}
                              </div>
                            )}
                            <p className="text-sm text-slate-700 whitespace-pre-wrap">{note.content}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>

                  {/* Add Note */}
                  <div className="flex flex-col gap-2" data-testid="new-note-container">
                    <Textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Add a note… (Enter = new line · Cmd/Ctrl+Enter to send)"
                      rows={3}
                      className="resize-y min-h-[80px]"
                      onKeyDown={(e) => {
                        // Cmd/Ctrl+Enter submits; plain Enter inserts a newline.
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                          e.preventDefault();
                          handleAddNote();
                        }
                      }}
                      data-testid="new-note-input"
                    />
                    <div className="flex items-center justify-between gap-2">
                      {isKR ? (
                        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer" data-testid="note-internal-toggle-wrap">
                          <input
                            type="checkbox"
                            checked={newNoteInternal}
                            onChange={(e) => setNewNoteInternal(e.target.checked)}
                            className="rounded border-slate-300"
                            data-testid="note-internal-toggle"
                          />
                          🔒 Internal note (only KR sees this)
                        </label>
                      ) : <span />}
                      <Button
                        type="button"
                        onClick={handleAddNote}
                        disabled={!newNote.trim()}
                        data-testid="new-note-send-btn"
                      >
                        <Send size={14} className="mr-1.5" /> Add Note
                      </Button>
                    </div>
                  </div>
                </TabsContent>

                {/* Tasks Tab Content */}
                <TabsContent value="tasks" className="mt-4 space-y-3">
                  <ScrollArea className="h-48 border rounded-lg p-3">
                    {loadingTasks ? (
                      <div className="flex items-center justify-center h-full">
                        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                      </div>
                    ) : tasks.length === 0 ? (
                      <p className="text-center text-slate-400 text-sm py-8">No tasks yet</p>
                    ) : (
                      <div className="space-y-2">
                        {/* Pending Tasks */}
                        {pendingTasks.length > 0 && (
                          <div className="space-y-2">
                            {pendingTasks.map((task) => (
                              <div key={task.task_id} className="flex items-start gap-3 p-2 bg-slate-50 rounded-lg group" data-testid={`task-${task.task_id}`}>
                                <Checkbox
                                  checked={false}
                                  onCheckedChange={() => handleToggleTask(task)}
                                  className="mt-0.5"
                                  data-testid={`task-checkbox-${task.task_id}`}
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-slate-900">{task.title}</p>
                                  <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                                    {task.assignee_name && (
                                      <span className="flex items-center gap-1.5">
                                        <UserAvatar userId={task.assignee_user_id} name={task.assignee_name} size="xs" />
                                        {task.assignee_name}
                                      </span>
                                    )}
                                    {task.due_date && (
                                      <span className="flex items-center gap-1">
                                        <Calendar size={10} />
                                        {formatDate(task.due_date)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 h-6 w-6 p-0"
                                  onClick={() => handleDeleteTask(task.task_id)}
                                >
                                  <X size={12} />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Completed Tasks */}
                        {completedTasks.length > 0 && (
                          <div className="space-y-2 mt-3">
                            <p className="text-xs text-slate-500 font-medium">Completed</p>
                            {completedTasks.map((task) => (
                              <div key={task.task_id} className="flex items-start gap-3 p-2 bg-green-50 rounded-lg group opacity-75" data-testid={`task-${task.task_id}`}>
                                <Checkbox
                                  checked={true}
                                  onCheckedChange={() => handleToggleTask(task)}
                                  className="mt-0.5"
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-slate-700 line-through">{task.title}</p>
                                  <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                                    {task.assignee_name && (
                                      <span className="flex items-center gap-1.5">
                                        <UserAvatar userId={task.assignee_user_id} name={task.assignee_name} size="xs" />
                                        {task.assignee_name}
                                      </span>
                                    )}
                                    {task.completed_at && (
                                      <span>Completed {formatDate(task.completed_at)}</span>
                                    )}
                                  </div>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 h-6 w-6 p-0"
                                  onClick={() => handleDeleteTask(task.task_id)}
                                >
                                  <X size={12} />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </ScrollArea>

                  {/* Add Task */}
                  {!showTaskForm ? (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => setShowTaskForm(true)}
                      data-testid="add-task-btn"
                    >
                      <Plus size={14} className="mr-2" />
                      Add Task
                    </Button>
                  ) : (
                    <div className="border rounded-lg p-3 space-y-3 bg-slate-50">
                      <Input
                        value={newTask.title}
                        onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="Task title..."
                        autoFocus
                        data-testid="new-task-title"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs text-slate-500">Assignee (optional)</Label>
                          <Select
                            value={newTask.assignee_user_id || '__none__'}
                            onValueChange={(value) => setNewTask(prev => ({ ...prev, assignee_user_id: value === '__none__' ? '' : value }))}
                          >
                            <SelectTrigger className="h-8 text-sm" data-testid="task-assignee-select">
                              <SelectValue placeholder="Unassigned" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">Unassigned</SelectItem>
                              {getAllUsers().map(u => (
                                <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-slate-500">Due Date (optional)</Label>
                          <Input
                            type="date"
                            value={newTask.due_date}
                            onChange={(e) => setNewTask(prev => ({ ...prev, due_date: e.target.value }))}
                            className="h-8 text-sm"
                            data-testid="task-due-date"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setShowTaskForm(false);
                            setNewTask({ title: '', assignee_user_id: '', due_date: '' });
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          onClick={handleAddTask}
                          disabled={!newTask.title.trim()}
                          data-testid="save-task-btn"
                        >
                          Add Task
                        </Button>
                      </div>
                    </div>
                  )}
                </TabsContent>
              </Tabs>

              {isKR && (
                <>
                  <Separator />
                  <div className="space-y-4">
                    <h4 className="font-heading font-medium">Update Ticket</h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Status</Label>
                        <Select
                          value={updateForm.status}
                          onValueChange={(value) => setUpdateForm(prev => ({ ...prev, status: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Open">Open</SelectItem>
                            <SelectItem value="In_Progress">In Progress</SelectItem>
                            <SelectItem value="Waiting_User">Waiting on User</SelectItem>
                            <SelectItem value="Waiting_On_Agent">Waiting on Agent</SelectItem>
                            <SelectItem value="Closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Assign To</Label>
                        <Select
                          value={updateForm.assigned_to_user_id || '__none__'}
                          onValueChange={(value) => setUpdateForm(prev => ({ ...prev, assigned_to_user_id: value === '__none__' ? '' : value }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Unassigned" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">Unassigned</SelectItem>
                            {getKRUsers().map(u => (
                              <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Priority</Label>
                        <Select
                          value={updateForm.priority || 'medium'}
                          onValueChange={(value) => setUpdateForm(prev => ({ ...prev, priority: value }))}
                        >
                          <SelectTrigger data-testid="update-priority-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TICKET_PRIORITIES.map(p => (
                              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Category</Label>
                        <Select
                          value={updateForm.category || ''}
                          onValueChange={(value) => setUpdateForm(prev => ({ ...prev, category: value }))}
                        >
                          <SelectTrigger data-testid="update-category-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TICKET_CATEGORIES.map(c => (
                              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Related Agent</Label>
                      <PayeeSelector
                        kind="agent"
                        value={updateForm.npn}
                        onChange={(npn) => setUpdateForm(prev => ({ ...prev, npn: npn || '' }))}
                        placeholder="Search by name or NPN…"
                        testId="update-agent-picker"
                      />
                      <div className="flex items-center justify-between">
                        <p className="text-[10px] text-slate-500">
                          Link or relink the agent this ticket is about.
                        </p>
                        {updateForm.npn && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 text-xs text-slate-500 hover:text-red-600"
                            onClick={() => setUpdateForm(prev => ({ ...prev, npn: '' }))}
                            data-testid="update-agent-clear-btn"
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          
          <DialogFooter className="border-t pt-4">
            <div className="flex justify-between w-full">
              {isKR && (
                <Button variant="destructive" onClick={() => {
                  setTicketToDelete(selectedTicket);
                  setShowDeleteDialog(true);
                }}>
                  <Trash2 size={14} className="mr-1" />
                  Delete
                </Button>
              )}
              <div className="flex gap-2 ml-auto">
                <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
                  Close
                </Button>
                {isKR && (
                  <Button onClick={handleUpdate}>
                    Update Ticket
                  </Button>
                )}
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 27.53.H — Reply From Hub compose dialog */}
      {selectedTicket && (
        <ReplyComposeDialog
          open={replyOpen}
          onOpenChange={setReplyOpen}
          seedNote={replySeed}
          sendReply={(payload) => ticketAPI.reply(selectedTicket.ticket_id, payload)}
          onSent={() => loadNotes(selectedTicket.ticket_id)}
          testidPrefix="ticket-reply"
        />
      )}
    </div>
  );
}
